/* eslint-disable @nx/enforce-module-boundaries */
import { Routes } from '@angular/router';

import { VANILLA_LAZY_ROUTES, VANILLA_SECONDARY_ROUTES } from '@frontend/loaders/routes';
import {
    GermanSupportGuard,
    IRetailConfig,
    IRouteData,
    LazyRouteGuard,
    LoggedInGuard,
    MaintenanceGuard,
    MaintenanceResolver,
    NotFoundPageGuard,
    RgyCheckGuard,
    RgyMatchGuard,
    SignUpRouteGuard,
} from '@frontend/oxygen/core';
import { rgyellow } from '@frontend/oxygen/core-bma';
import { FanzoneAuthGuard } from '@frontend/oxygen/core-fanzone';
import { ProductSwitchResolver } from '@frontend/oxygen/core-lib';
import { ROOT_APP_ROUTES } from '@frontend/oxygen/core-vanillainit';
import { ILadbrokesRetailConfig } from '@frontend/oxygen/lm-core';
import { routeData } from '@frontend/vanilla/core';

import { RootComponent } from '../../../../../ladbrokes-desktop-app/app.component';
import { routes as OlympicsRoutes } from '../../../../../ladbrokes-desktop-app/src/olympics/olympics-route.module';
import { routes as RacingRoutes } from '../../../../../ladbrokes-desktop-app/src/racing/racing-routing.module';
import { routes as SbRoutes } from '../../../../../ladbrokes-desktop-app/src/sb/sb-routing.module';

export const appLDRoutes: Routes = [
    {
        path: '',
        component: RootComponent,
        data: {
            ...routeData({ allowAnonymous: true }),
            product: 'host',
            segment: 'main',
        },
        runGuardsAndResolvers: 'always',
        resolve: {
            __p: ProductSwitchResolver,
        },
        // canActivate: [SiteRootGuard],
        children: [
            {
                path: 'en',
                data: {
                    segment: 'vanilla',
                },
                children: [
                    ...VANILLA_LAZY_ROUTES,
                    ...ROOT_APP_ROUTES,
                    {
                        path: '**',
                        redirectTo: '/',
                    },
                ],
            },
            ...SbRoutes,
            ...RacingRoutes,
            ...OlympicsRoutes,
            {
                path: 'bet-finder',
                canActivate: [GermanSupportGuard],
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/bf/betfinder.module').then((m) => m.BetFinderModule),
                data: {
                    feature: 'raceBetFinder',
                } as IRouteData<ILadbrokesRetailConfig>,
            },
            {
                path: '',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/ladbrokes-desktop-home/ladbrokes-desktop-home.module').then(
                        (m) => m.LadbrokesDesktopHomeModule,
                    ),
                data: {
                    segment: 'home',
                    preload: true,
                },
            },
            {
                path: 'logout',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/ladbrokes-desktop-home/ladbrokes-desktop-home.module').then(
                        (m) => m.LadbrokesDesktopHomeModule,
                    ),
                data: {
                    segment: 'logout',
                    preload: true,
                },
            },
            {
                path: 'deposit',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/ladbrokes-desktop-home/ladbrokes-desktop-home.module').then(
                        (m) => m.LadbrokesDesktopHomeModule,
                    ),
                data: {
                    preload: true,
                },
            },
            {
                path: 'contact-us',
                loadChildren: () => import('@frontend/oxygen/core-contact-us').then((m) => m.ContactUsModule),
                data: {
                    preload: true,
                },
            },
            {
                path: 'live-stream',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/live-stream/live-stream.module').then((m) => m.LiveStreamModule),
                data: {
                    preload: true,
                },
            },
            {
                path: 'az-sports',
                data: {
                    segment: 'azSports',
                },
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/aToZMenu/aToZ-sports-page.module').then(
                        (m) => m.LazyAToZSportPageModule,
                    ),
            },
            {
                path: 'settings',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/bma/components/userSettings/user-settings.module').then(
                        (m) => m.UserSettingsModule,
                    ),
                canActivate: [LoggedInGuard],
                runGuardsAndResolvers: 'always',
                data: {
                    segment: 'settings',
                },
            },
            {
                path: 'static/:static-block',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/static/static.module').then((m) => m.StaticModule),
                data: {
                    preload: true,
                },
            },
            {
                path: 'freebets',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/freebets/freebets.module').then((m) => m.FreebetsModule),
                canActivate: [LoggedInGuard],
                data: {
                    segment: 'freebets',
                },
            },
            {
                path: '404',
                loadChildren: () => import('@frontend/oxygen/core-notfoundmodule').then((m) => m.NotFoundModule),
                data: {
                    segment: '404',
                },
            },
            {
                path: 'under-maintenance',
                loadComponent: () => import('@frontend/oxygen/core-shared').then((m) => m.MaintenanceComponent),
                resolve: { data: MaintenanceResolver },
                canDeactivate: [MaintenanceGuard],
            },
            // Modules lazy loaded by routes
            // Yourcall
            {
                path: 'yourcall-lazy-load',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/yourCall/your-call.module').then((m) => m.YourCallModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'tote-information',
                canActivate: [GermanSupportGuard],
                children: [],
            },
            {
                path: 'tote',
                canActivate: [GermanSupportGuard],
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/tote/tote.module').then((m) => m.ToteModule),
            },
            //SDM TODO: commented temp to fix LD host app build issue
            {
                path: 'virtual-sports',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/vsbr/vsbr.module').then((m) => m.VsbrModule),
            },
            {
                path: 'in-play',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/inPlay/inplay.module').then((m) => m.InplayModule),
                data: {
                    preload: true,
                    loadAfterSeconds: 0,
                },
            },
            {
                path: 'byb-module',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/bybHistory/byb-history.module').then((m) => m.LazyBybHistoryModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'betslip/add/:outcomeId',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/betslip/betslip.module').then((m) => m.BetslipModule),
            },
            {
                path: 'betslip/unavailable',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/betslip/betslip.module').then((m) => m.BetslipModule),
            },
            {
                path: 'competitions/:sport/:className/:typeName',
                data: {
                    segment: 'competitionTypeEvents',
                },
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/competitionsSportTab/competitionsSportTab.module').then(
                        (m) => m.CompetitionsTabModule,
                    ),
            },
            {
                path: 'competition-filters',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/competitionFilters/competitionFilters.module').then(
                        (m) => m.CompetitionFiltersModule,
                    ),
            },
            // Odds Boost module lazy loading
            {
                path: 'oddsboost',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/oddsBoost/odds-boost.module').then((m) => m.OddsBoostModule),
            },
            // Bet History module lazy loading
            {
                path: 'cashout',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/betHistory/bet-history.module').then((m) => m.BetHistoryModule),
            },
            {
                path: 'open-bets',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/betHistory/bet-history.module').then((m) => m.BetHistoryModule),
            },
            {
                path: 'bet-history',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/betHistory/bet-history.module').then((m) => m.BetHistoryModule),
            },
            {
                path: 'in-shop-bets',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/betHistory/bet-history.module').then((m) => m.BetHistoryModule),
            },
            // Lotto module lazy loading
            // {
            //   path: 'lotto-results',
            //  canActivate: [GermanSupportGuard],
            //  children: []
            // },
            {
                path: 'lotto',
                canActivate: [GermanSupportGuard],
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/lotto/lotto.module').then((m) => m.LottoModule),
            },
            {
                path: 'big-competition',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/bigCompetitions/big-competitions.module').then((m) => m.BigCompetitionsModule),
            },
            // Registration module lazy loading
            {
                path: 'signup',
                canActivate: [SignUpRouteGuard],
                children: [],
            },
            // Retail module lazy loading
            {
                path: 'retail',
                /* below 2 lines added for grid decommision in sports book*/
                redirectTo: '/shop-locator',
                pathMatch: 'full',
            },
            {
                path: 'retail-upgrade',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/retail/retail.module').then((m) => m.RetailModule),
                data: {
                    feature: 'upgrade',
                } as IRouteData<IRetailConfig>,
            },
            {
                path: 'bet-filter',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/retail/retail.module').then((m) => m.RetailModule),
                data: {
                    feature: 'footballFilter',
                } as IRouteData<ILadbrokesRetailConfig>,
            },
            {
                path: 'bet-filter/:child',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/retail/retail.module').then((m) => m.RetailModule),
                data: {
                    feature: 'footballFilter',
                } as IRouteData<ILadbrokesRetailConfig>,
            },
            {
                path: 'bet-filter/:child/:child',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/retail/retail.module').then((m) => m.RetailModule),
                data: {
                    feature: 'footballFilter',
                } as IRouteData<ILadbrokesRetailConfig>,
            },
            {
                path: 'bet-filter',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/retail/retail.module').then((m) => m.RetailModule),
                data: {
                    feature: 'digitalCoupons',
                } as IRouteData<ILadbrokesRetailConfig>,
            },
            {
                path: 'digital-coupons',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/retail/retail.module').then((m) => m.RetailModule),
                data: {
                    feature: 'digitalCoupons',
                } as IRouteData<ILadbrokesRetailConfig>,
            },
            {
                path: 'saved-betcodes',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/retail/retail.module').then((m) => m.RetailModule),
                data: {
                    feature: 'savedBetCodes',
                } as IRouteData<ILadbrokesRetailConfig>,
            },
            {
                path: 'bet-tracker',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/retail/retail.module').then((m) => m.RetailModule),
                data: {
                    feature: 'shopBetTracker',
                } as IRouteData<IRetailConfig>,
            },
            {
                path: 'shop-locator',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/retail/retail.module').then((m) => m.RetailModule),
                data: {
                    feature: 'shopLocator',
                } as IRouteData<IRetailConfig>,
            },
            // Freebets lazy loading
            {
                path: 'freebets-lazy-load',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/freebets/freebets.module').then((m) => m.FreebetsModule),
                canActivate: [LazyRouteGuard],
            },
            // Quickbet
            {
                path: 'quickbet-lazy-load',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/quickbet/quickbet.module').then((m) => m.QuickbetModule),
                canActivate: [LazyRouteGuard],
            },
            // Quick Deposit
            {
                path: 'quick-deposit-lazy-load',
                loadChildren: () => import('@frontend/oxygen/lm-quickdeposit').then((m) => m.QuickDepositModule),
                canActivate: [LazyRouteGuard],
            },
            // Bet radar lazy loading
            {
                path: 'bet-radar-lazy-load',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/betRadarProvider/bet-radar.module').then(
                        (m) => m.BetRadarLadbrokesModule,
                    ),
                canActivate: [LazyRouteGuard],
            },
            // Receipt header lazy loading
            {
                path: 'receipt-header-lazy-load',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/receiptHeader/receipt-header.module').then(
                        (m) => m.ReceiptHeaderModule,
                    ),
                canActivate: [LazyRouteGuard],
            },
            // Racing Post lazy loading
            {
                path: 'racing-post-lazy-load',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/racingPostTip/racing-post-tip.module').then(
                        (m) => m.RacingPostTipModule,
                    ),
                canActivate: [LazyRouteGuard],
            },
            // Next Race for Betslip lazy loading
            {
                path: 'next-races-lazy-load',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/nextRacesToBetslip/nextraces-to-betslip.module').then(
                        (m) => m.NextRacesToBetslipModule,
                    ),
                canActivate: [LazyRouteGuard],
            },
            // Runner-spotlight lazy loading
            {
                path: 'runner-spotlight-lazy-load',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/runnerSpotlight/runner-spotlight.module').then(
                        (m) => m.RunnerSpotlightModule,
                    ),
                canActivate: [LazyRouteGuard],
            },
            // Runner-spotlight lazy loading
            {
                path: 'racing-post-verdict-lazy-load',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/racingPostVerdict/racing-post-verdict.module').then(
                        (m) => m.RacingPostVerdictModule,
                    ),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'forecast-tricast-lazy-load',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/forecastTricast/forecastTricast.module').then(
                        (m) => m.ForecastTricastModule,
                    ),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'market-description-lazy-load',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/market-description/market-description.module').then(
                        (m) => m.MarketDescriptionModule,
                    ),
                canActivate: [LazyRouteGuard],
            },
            //SDM TODO: commented temp to fix LD host app build issue
            // {
            //     path: 'fiveAside-entry-confirmation-lazy-load',
            //     loadChildren: () =>
            //         import('../../../../../ladbrokes-desktop-app/src/lazy-modules/fiveASideShowDown/fiveaside-entry-confirmation.module').then((m) => m.FiveASideEntryConfirmationModule),
            //     canActivate: [LazyRouteGuard],
            // },
            {
                path: 'event-video-stream-lazy-load',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/eventVideoStream/event-video-stream.module').then(
                        (m) => m.LazyEventVideoStreamModule,
                    ),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'promotions',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/promotions/promotions.module').then((m) => m.PromotionsModule),
                data: {
                    path: 'promotions/retail',
                    feature: 'promotions',
                    moduleName: rgyellow.PROMOTIONS,
                },
                canMatch: [RgyMatchGuard],
                canActivate: [RgyCheckGuard],
            },
            //SDM TODO: commented temp to fix LD host app build issue
            // ShowDown Module (New Feature)
            // {
            //     path: '5-a-side',
            //     loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/fiveASideShowDown/fiveASideShowDown.module').then((m) => m.FiveASideShowDownModule),
            //     data: {
            //         feature: 'show-down',
            //     },
            // },
            {
                path: 'matchday-rewards',
                loadChildren: () => import('@frontend/oxygen/core-euromodule').then((m) => m.EuroModule),
            },
            // EDP
            {
                path: 'edp-lazy-load',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/edp/edp.module').then((m) => m.EdpModule),
                canActivate: [LazyRouteGuard],
                data: {
                    preload: true,
                    loadAfterSeconds: 0,
                },
            },
            // Banners Module
            {
                path: 'banners-section',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/banners/banners.module').then((m) => m.BannersModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: '1-2-free',
                pathMatch: 'full',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/oneTwoFree/one-two-free.module').then((m) => m.LDOneTwoFreeModule),
                canMatch: [RgyMatchGuard],
                data: { moduleName: rgyellow.ONE_TWO_FREE },
            },
            {
                path: 'qe/:sourceId',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/questionEngine/question-engine.module').then((m) => m.QuestionEngineModule),
                data: {
                    segment: 'question-engine',
                },
            },
            {
                path: 'seoStaticBlock-lazy-load',
                loadChildren: () => import('@frontend/oxygen/core-seostaticblockmodule').then((m) => m.SeoStaticBlockModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'maxpayout-error-container',
                loadChildren: () => import('@frontend/oxygen/lm-maxpayouterrorcontainer').then((m) => m.MaxpayoutErrorContainerModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'fanzone',
                canActivate: [FanzoneAuthGuard],
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/fanzone/fanzone.module').then((m) => m.FanzoneModule),
            },
            {
                path: 'luckydip',
                loadChildren: () =>
                    import('../../../../../ladbrokes-desktop-app/src/lazy-modules/luckyDip/lucky-dip.module').then((m) => m.LuckyDipModule),
            },
            // Olympics lazy loading
            {
                path: 'olympics',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/olympics/olympics.module').then((m) => m.OlympicsModule),
            },
            {
                path: 'extra-place-signposting',
                loadChildren: () => import('@frontend/oxygen/core-extraplacesignpostingmodule').then((m) => m.ExtraPlaceSignpostingModule),
                canActivate: [LazyRouteGuard],
            },
            {
                path: 'my-stable',
                loadChildren: () => import('../../../../../ladbrokes-desktop-app/src/myStable/mystable.module').then((m) => m.MyStableModule),
                canActivate: [LoggedInGuard],
            },
            {
                path: '**',
                loadChildren: () => import('@frontend/oxygen/core-notfoundmodule').then((m) => m.NotFoundModule),
                canActivate: [NotFoundPageGuard],
            },
        ],
    },
    ...VANILLA_SECONDARY_ROUTES,
];
